import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { FolderOpen, Loader2, RefreshCw } from 'lucide-react';
import { apiFetch } from '../utils/api';
import { useI18n } from '../hooks/useI18n';

declare global {
  interface Window {
    gapi: any;
    google: any;
  }
}

interface PickerConfig {
  api_key: string;
  app_id: string;
  access_token: string;
  expires_at: string;
}

interface FolderSelection {
  folderId: string;
  folderName: string;
  driveId?: string; // For Shared Drives
}

interface GoogleDrivePickerProps {
  onFolderSelected: (selection: FolderSelection) => void;
  disabled?: boolean;
  className?: string;
}

export default function GoogleDrivePicker({ 
  onFolderSelected, 
  disabled = false,
  className = ''
}: GoogleDrivePickerProps) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [pickerLoaded, setPickerLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load Google Picker API script
  useEffect(() => {
    if (window.google?.picker) {
      setPickerLoaded(true);
      return;
    }

    let mounted = true;

    const loadScript = (src: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        const existingScript = document.querySelector(`script[src="${src}"]`);
        if (existingScript) {
          // Script tag exists, wait for it to load
          if ((existingScript as HTMLScriptElement).dataset.loaded === 'true') {
            resolve();
          } else {
            existingScript.addEventListener('load', () => resolve());
            existingScript.addEventListener('error', () => reject(new Error(`Failed to load script: ${src}`)));
          }
          return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          script.dataset.loaded = 'true';
          resolve();
        };
        script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
        document.head.appendChild(script);
      });
    };

    const waitForGapi = (maxAttempts = 50): Promise<void> => {
      return new Promise((resolve, reject) => {
        let attempts = 0;
        const check = () => {
          if (window.gapi) {
            resolve();
          } else if (attempts >= maxAttempts) {
            reject(new Error('Timeout waiting for gapi'));
          } else {
            attempts++;
            setTimeout(check, 100);
          }
        };
        check();
      });
    };

    const loadPickerApi = async () => {
      try {
        // Load the Google API loader
        await loadScript('https://apis.google.com/js/api.js');
        
        // Wait for gapi to be available
        await waitForGapi();
        
        // Load the picker library
        await new Promise<void>((resolve, reject) => {
          window.gapi.load('picker', {
            callback: () => resolve(),
            onerror: () => reject(new Error('Failed to load picker'))
          });
        });
        
        if (mounted) {
          setPickerLoaded(true);
        }
      } catch (err) {
        console.error('Failed to load Google Picker API:', err);
        // Only show error if we're still mounted and it's a real failure
        if (mounted) {
          setError('Failed to load Google Picker');
        }
      }
    };

    loadPickerApi();

    return () => {
      mounted = false;
    };
  }, []);

  const openPicker = useCallback(async () => {
    if (!pickerLoaded) {
      setError('Google Picker is still loading');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get picker config from backend (includes access token)
      const response = await apiFetch('/api/gdrive-oauth/picker-config');
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Please connect your Google account first');
        } else {
          const data = await response.json();
          setError(data.detail || 'Failed to get picker configuration');
        }
        return;
      }

      const config: PickerConfig = await response.json();

      // Build the picker
      const picker = new window.google.picker.PickerBuilder()
        .setAppId(config.app_id)
        .setOAuthToken(config.access_token)
        .setDeveloperKey(config.api_key)
        .setTitle('Select a folder for invoices')
        // My Drive folders
        .addView(
          new window.google.picker.DocsView()
            .setIncludeFolders(true)
            .setSelectFolderEnabled(true)
            .setMimeTypes('application/vnd.google-apps.folder')
        )
        // Shared Drives
        .addView(
          new window.google.picker.DocsView()
            .setIncludeFolders(true)
            .setSelectFolderEnabled(true)
            .setMimeTypes('application/vnd.google-apps.folder')
            .setEnableDrives(true)
        )
        // Shared with me
        .addView(
          new window.google.picker.DocsView(window.google.picker.ViewId.DOCS)
            .setIncludeFolders(true)
            .setSelectFolderEnabled(true)
            .setMimeTypes('application/vnd.google-apps.folder')
            .setOwnedByMe(false)
        )
        .enableFeature(window.google.picker.Feature.SUPPORT_DRIVES)
        .setCallback((data: any) => {
          if (data.action === window.google.picker.Action.PICKED) {
            const doc = data.docs[0];
            const selection: FolderSelection = {
              folderId: doc.id,
              folderName: doc.name,
            };
            
            // Check if it's from a Shared Drive
            if (doc.driveId) {
              selection.driveId = doc.driveId;
            }
            
            onFolderSelected(selection);
          }
        })
        .build();

      picker.setVisible(true);

    } catch (err) {
      console.error('Error opening picker:', err);
      setError('Failed to open folder picker');
    } finally {
      setLoading(false);
    }
  }, [pickerLoaded, onFolderSelected]);

  return (
    <div className={className}>
      <Button
        type="button"
        variant="outline"
        onClick={openPicker}
        disabled={disabled || loading || !pickerLoaded}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <FolderOpen className="h-4 w-4 mr-2" />
        )}
        {loading ? t('gdrive.opening', 'common') : t('gdrive.selectFolder', 'common')}
      </Button>
      
      {error && (
        <p className="text-sm text-red-500 mt-2">{error}</p>
      )}
      
      {!pickerLoaded && !error && (
        <p className="text-sm text-muted-foreground mt-2">
          {t('gdrive.loadingPicker', 'common')}
        </p>
      )}
    </div>
  );
}
